import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';

export default function AuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const code = searchParams.get('code');
    const errorParam = searchParams.get('error');
    const errorDesc = searchParams.get('error_description');

    if (errorParam) {
      setError(errorDesc || errorParam);
      setProcessing(false);
      return;
    }

    if (!code) {
      setError('No authorization code received');
      setProcessing(false);
      return;
    }

    const redirectUri = `${window.location.origin}/auth/callback`;

    const exchangeCode = async () => {
      try {
        const res = await api.post('/auth/microsoft/callback', { code, redirectUri });
        // httpOnly cookie is set by backend — no client-side token storage needed
        const { user } = res.data;
        setUser(user);
        navigate(user.onboarded ? '/' : '/onboarding', { replace: true });
      } catch (err) {
        const detail = err.response?.data?.detail || 'Microsoft login failed';
        setError(detail);
        setProcessing(false);
      }
    };

    exchangeCode();
  }, [searchParams, navigate, setUser]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--pd-bg)' }}>
        <div className="max-w-md w-full rounded-[28px] p-8 text-center" style={{ background: 'var(--pd-panel)', border: '1px solid var(--pd-border)' }}>
          <div className="w-12 h-12 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: 'var(--pd-crimson-soft)' }}>
            <span className="text-xl" style={{ color: 'var(--pd-crimson)' }}>!</span>
          </div>
          <h2 className="text-[20px] font-semibold mb-2" style={{ color: 'var(--pd-text)' }}>Sign-in failed</h2>
          <p className="text-[13px] mb-6" style={{ color: 'var(--pd-muted)', lineHeight: 1.7 }}>{error}</p>
          <button
            data-testid="auth-callback-retry"
            onClick={() => navigate('/login', { replace: true })}
            className="px-6 py-3 rounded-[18px] text-[13px] font-semibold text-white"
            style={{ background: 'var(--pd-focus)' }}
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--pd-bg)' }}>
      <div className="text-center">
        <div className="w-10 h-10 mx-auto mb-4 border-3 rounded-full animate-spin" style={{ borderColor: 'var(--pd-border)', borderTopColor: 'var(--pd-focus)' }} />
        <p className="text-[14px] font-medium" style={{ color: 'var(--pd-text)' }}>Signing you in...</p>
        <p className="text-[12px] mt-1" style={{ color: 'var(--pd-muted)' }}>Verifying your Microsoft account</p>
      </div>
    </div>
  );
}
